import { useState } from "react";
import { toast } from "sonner";
import { DownloadSimple, CircleNotch, X, ArrowsClockwise, ClockClockwise } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { DatePicker } from "@/components/ui/date-picker";

/**
 * Guest list export by reservation state.
 *
 * Croatian rentals often re-shuffle bookings on the calendar after they land
 * (moving a guest to a different apartment). For tourist-board reporting and
 * OTA commission reconciliation you need to know what was ORIGINALLY booked;
 * for day-to-day housekeeping you need to know where guests actually sleep.
 *
 *   "current"  → whatever the calendar shows RIGHT NOW (post-moves)
 *   "original" → the snapshot recorded at booking time (no moves)
 *
 * Both modes stream through the same `/api/guests/by-reservations` endpoint;
 * this component only picks the mode and downloads the CSV.
 */
export default function ReservationExportButton() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("current");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [channel, setChannel] = useState("");
  const [busy, setBusy] = useState(false);

  const download = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const params = new URLSearchParams({ mode });
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (channel) params.set("channel", channel);
      const res = await api.get(`/guests/by-reservations?${params.toString()}`);
      const rows = res.data?.rows || [];
      if (rows.length === 0) {
        toast.info("Nema rezervacija u odabranom rasponu.");
        setBusy(false);
        return;
      }
      // Build CSV
      const cols = [
        ["code", "Kod rez."],
        ["guest_name", "Gost"],
        ["guest_email", "Email"],
        ["guest_phone", "Telefon"],
        ["check_in", "Dolazak"],
        ["check_out", "Odlazak"],
        ["nights", "Noći"],
        ["adults", "Odraslih"],
        ["children", "Djece"],
        ["source", "Kanal"],
        ["property_name", "Objekt"],
        ["unit_code", "Jedinica"],
        ["total_amount", "Iznos"],
        ["status", "Status"],
        ["payment_status", "Plaćanje"],
      ];
      const esc = (v) => {
        if (v == null) return "";
        const s = String(v).replace(/"/g, '""');
        return /[";\n]/.test(s) ? `"${s}"` : s;
      };
      const header = cols.map((c) => c[1]).join(";");
      const body = rows.map((r) => cols.map((c) => esc(r[c[0]])).join(";")).join("\n");
      const csv = "\uFEFF" + header + "\n" + body; // UTF-8 BOM for Excel
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `gosti-${mode === "original" ? "kako-rezervirano" : "trenutno"}-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Ispisano ${rows.length} rezervacija.`);
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex h-10 shrink-0 items-center gap-2 rounded-md border border-border bg-card px-3 text-sm font-medium text-foreground hover:bg-muted"
        title="Izvoz rezervacija (CSV)"
        data-testid="guests-export-btn"
      >
        <DownloadSimple size={16} /> <span className="hidden sm:inline">Izvoz</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setOpen(false)} />
          <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-card p-5 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h4 className="text-base font-semibold">Ispis rezervacija</h4>
              <button onClick={() => setOpen(false)} className="rounded-md p-1 hover:bg-muted"><X size={16} /></button>
            </div>

            <form onSubmit={download} className="space-y-4">
              <div>
                <p className="mb-2 text-xs font-medium text-foreground">Tip ispisa</p>
                <div className="grid grid-cols-1 gap-2">
                  <button
                    type="button"
                    onClick={() => setMode("current")}
                    className={`flex items-start gap-2 rounded-md border p-3 text-left ${mode === "current" ? "border-primary bg-primary/5" : "border-border hover:bg-muted"}`}
                    data-testid="export-mode-current"
                  >
                    <ArrowsClockwise size={18} weight="duotone" className="mt-0.5 shrink-0 text-primary" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">Trenutno stanje u kalendaru</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">Gost je u jedinici gdje je sada nakon svih rotacija</p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode("original")}
                    className={`flex items-start gap-2 rounded-md border p-3 text-left ${mode === "original" ? "border-primary bg-primary/5" : "border-border hover:bg-muted"}`}
                    data-testid="export-mode-original"
                  >
                    <ClockClockwise size={18} weight="duotone" className="mt-0.5 shrink-0 text-primary" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">Kako je rezervirano</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">Originalna jedinica i objekt iz OTA rezervacije</p>
                    </div>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="mb-1 block text-xs font-medium">Od (opcionalno)</span>
                  <DatePicker value={dateFrom} onChange={setDateFrom} placeholder="Od datuma" data-testid="export-date-from" />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-medium">Do (opcionalno)</span>
                  <DatePicker value={dateTo} onChange={setDateTo} placeholder="Do datuma" data-testid="export-date-to" />
                </label>
              </div>

              <label className="block">
                <span className="mb-1 block text-xs font-medium">Kanal (opcionalno)</span>
                <select value={channel} onChange={(e) => setChannel(e.target.value)} className="rw-input" data-testid="export-channel">
                  <option value="">Svi kanali</option>
                  <option value="direct">Direktno</option>
                  <option value="booking_com">Booking.com</option>
                  <option value="airbnb">Airbnb</option>
                  <option value="expedia">Expedia</option>
                  <option value="agoda">Agoda</option>
                </select>
              </label>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium hover:bg-muted">Odustani</button>
                <button type="submit" disabled={busy} className="flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50" data-testid="export-submit">
                  {busy ? <CircleNotch size={14} className="animate-spin" /> : <DownloadSimple size={14} weight="bold" />}
                  Preuzmi CSV
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </>
  );
}
